<template>
  <div>
    <page-header :title="$t('compute.modelarts.create')" />
    <page-body needMarginBottom>
      <a-form :form="form.fc" class="mt-3" v-bind="formItemLayout" hideRequiredMark>
        <a-form-item :label="$t('compute.text_297', [$t('dictionary.project')])">
          <domain-project :decorators="decorators.projectDomain" :fc="form.fc" :labelInValue="false" />
        </a-form-item>
        <area-selects
          class="mb-0"
          ref="areaSelects"
          :wrapperCol="formItemLayout.wrapperCol"
          :labelCol="formItemLayout.labelCol"
          :names="['provider', 'cloudregion']"
          :cloudregionParams="cloudregionParams"
          :isRequired="true"
          :providerParams="providerParams"
          filterBrandResource="modelarts_pools"
          @change="handleRegionChange" />
        <a-form-item :label="$t('compute.text_15')">
          <base-select
            resource="cloudproviders"
            v-decorator="decorators.cloudprovider"
            :params="cloudproviderParams"
            :isDefaultSelect="true"
            :showSync="true"
            :select-props="{ placeholder: $t('compute.text_149') }" />
        </a-form-item>
        <a-form-item :label="$t('common.name')">
          <a-input v-decorator="decorators.name" />
          <template v-slot:extra>
            <name-repeated res="modelarts_pools" :name="form.fd.name" :default-text="$t('compute.text_893')" />
          </template>
        </a-form-item>
        <a-form-item :label="$t('common.description')">
          <a-input v-decorator="decorators.description" />
        </a-form-item>
        <a-form-item :label="$t('compute.modelarts.bill_type')">
          <a-radio-group v-decorator="decorators.bill_type">
            <a-radio-button v-for="item of BILL_TYPES" :value="item.key" :key="item.key">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item :label="$t('compute.modelarts.work_type')">
          <a-checkbox-group v-decorator="decorators.work_type">
            <a-checkbox v-for="item of WORK_TYPES" :value="item.key" :key="item.key">{{ item.label }}</a-checkbox>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item :label="$t('compute.modelarts.spec_arch')">
          <a-radio-group v-decorator="decorators.arch">
            <a-radio-button v-for="item of archOpts" :value="item.key" :key="item.key">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item :label="$t('compute.text_109')">
          <a-skeleton v-if="skuArchLoading && !skuArchLoaded" />
          <sku v-else v-decorator="decorators.sku"
            :provider="regionProvider"
            :region="regionId"
            :arch="form.fd.arch" />
        </a-form-item>
        <a-form-item :label="$t('ai.common.node_count')">
          <a-input-number v-decorator="decorators.node_count" :min="1" :max="200" :step="1" />
        </a-form-item>
        <a-form-item :label="$t('ai.common.cidr')">
          <a-input v-decorator="decorators.cidr" />
        </a-form-item>
        <a-form-item :label="$t('compute.modelarts.zone')">
          <a-radio-group v-decorator="decorators.zone_type">
            <a-radio-button v-for="item of ZONE_TYPES" :value="item.key" :key="item.key">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
      </a-form>
    </page-body>
    <page-footer>
      <div slot="right">
        <a-button class="mr-3" type="primary" @click="handleConfirm" :loading="loading">{{$t('compute.text_907')}}</a-button>
        <a-button @click="handleCancel">{{$t('compute.text_908')}}</a-button>
      </div>
    </page-footer>
  </div>
</template>

<script>
import DomainProject from '@/sections/DomainProject'
import { Manager } from '@/utils/manager'
import NameRepeated from '@/sections/NameRepeated'
import AreaSelects from '@/sections/AreaSelects'
import sku from '../sections/Sku'
import {
  ARCH_MAP,
  WORK_TYPES,
  WORK_TYPE,
  ZONE_TYPES,
  ZONE_TYPE_MAP,
  BILL_TYPES,
  BILL_TYPE_MAP,
} from '../constants'

export default {
  name: 'ModelArtsCreate',
  components: {
    DomainProject,
    NameRepeated,
    AreaSelects,
    sku,
  },
  data () {
    return {
      WORK_TYPES,
      ZONE_TYPES,
      BILL_TYPES,
      decorators: {
        projectDomain: {
          project: [
            'project',
            {
              initialValue: undefined,
            },
          ],
          domain: [
            'domain',
            {
              initialValue: undefined,
            },
          ],
        },
        cloudprovider: [
          'cloudprovider',
          {
            rules: [
              { required: true, message: this.$t('network.text_689') },
            ],
          },
        ],
        name: [
          'name',
          {
            rules: [
              { required: true, message: `${this.$t('common.placeholder')}${this.$t('common.name')}` },
              // { validator: this.$validate('resourceName') },
            ],
          },
        ],
        description: [
          'description',
        ],
        work_type: [
          'work_type',
          {
            initialValue: [WORK_TYPE.Notebook.key],
            rules: [
              { required: true, message: `${this.$t('common.tips.select', [this.$t('compute.modelarts.work_type')])}` },
            ],
          },
        ],
        arch: [
          'arch',
          {
            initialValue: ARCH_MAP.x86.key,
          },
        ],
        sku: [
          'sku',
          {
            rules: [
              { required: true, message: this.$t('compute.modelarts.sku.required_message') },
            ],
          },
        ],
        zone_type: [
          'zone_type',
          {
            initialValue: ZONE_TYPE_MAP.random.key,
          },
        ],
        bill_type: [
          'bill_type',
          {
            initialValue: BILL_TYPE_MAP.postpaid.key,
          },
        ],
        node_count: [
          'node_count',
          {
            initialValue: 1,
            rules: [
              { required: true, message: `${this.$t('common.placeholder')}${this.$t('ai.common.node_count')}` },
            ],
          },
        ],
        cidr: [
          'cidr',
        ],
      },
      form: {
        fc: this.$form.createForm(this, {
          onValuesChange: (props, values) => {
            Object.keys(values).forEach((key) => {
              this.form.fd[key] = values[key]
            })
          },
        }),
        fd: {
          name: '',
          cloudprovider: '',
          arch: 'x86',
        },
      },
      formItemLayout: {
        wrapperCol: {
          md: { span: 16 },
          xl: { span: 18 },
          xxl: { span: 20 },
        },
        labelCol: {
          md: { span: 8 },
          xl: { span: 6 },
          xxl: { span: 4 },
        },
      },
      skuArchLoading: false,
      skuArchLoaded: false,
      cloudregionParams: {
        scope: this.$store.getters.scope,
        usable: false,
      },
      providerParams: {
        scope: this.$store.getters.scope,
        usable: false,
        'brand.0': 'Huawei',
        'brand.1': 'HCSO',
      },
      regionProvider: 'Huawei',
      regionId: '',
      skuParam: {},
      archOpts: [],
    }
  },
  computed: {
    cloudproviderParams () {
      return {
        scope: this.$store.getters.scope,
        cloudregion: this.regionId,
        read_only: false,
      }
    },
  },
  created () {
    this.fetchSkusArch()
  },
  methods: {
    handleRegionChange (data) {
      const { cloudregion } = data
      if (cloudregion) {
        const { provider } = cloudregion.value
        this.regionProvider = provider
        this.regionId = cloudregion.id
      }
    },
    async fetchSkusArch () {
      try {
        this.skuArchLoading = true
        this.skuArchLoaded = false
        const manager = new Manager('modelarts_pool_skus')
        const { data } = await manager.get({
          id: 'distinct-field',
          params: {
            field: 'cpu_arch',
          },
        })
        this.archOpts = data.cpu_arch.map(item => {
          return {
            key: item,
            label: item,
          }
        })
        this.skuArchLoaded = true
      } catch (error) {
        throw error
      } finally {
        this.skuArchLoading = false
      }
    },
    generateValues (values) {
      const data = {
        generate_name: values.name,
        description: values.description,
        manager_id: values.cloudprovider,
        cloudregion: this.regionId,
        instance_type: values.sku?.name,
        work_type: values.work_type?.join(','),
        cpu_arch: values.arch,
        project_id: values.project,
        node_count: values.node_count,
        cidr: values.cidr,
      }
      return data
    },
    handleCancel () {
      this.$router.push({
        name: 'ModelArts',
      })
    },
    async handleConfirm () {
      const { validateFields } = this.form.fc
      const manager = new Manager('modelarts_pools')
      try {
        const values = await validateFields()
        this.loading = true
        await manager.create({
          data: Object.assign({}, this.generateValues(values)),
        })
        this.handleCancel()
      } catch (err) {
        throw err
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style>
</style>
